<template>
  <div
    class="relative w-full min-h-screen font-inter flex flex-col bg-black/50 backdrop-blur-xs overflow-hidden"
  >
    <!-- Background Image -->
    <img
      src="../assets/img/ab.png"
      alt="Background"
      class="absolute inset-0 object-cover w-full h-full z-0 opacity-85 bright"
    />

    <!-- Fixed Header -->
    <header
      class="fixed top-0 left-0 w-full bg-green1/70 backdrop-blur-2xl border-b border-white/20 text-white flex justify-between items-center px-6 py-3 z-40"
    >
      <p class="text-[13px] md:text-[15px] lg:text-[19px] font-light">
        DNSC Information Systems Gateway
      </p>

      <img
        src="../assets/img/dnsc-logo-white.png"
        alt="DNSC Logo"
        class="h-[30px] md:h-[45px] lg:h-[40px] cursor-pointer hover:scale-105 transition-transform duration-300"
        @click="toggleSetting"
      />
    </header>

    <!-- Scrollable Content -->
    <main
      class="flex-1 relative z-10 pt-[85px] overflow-y-auto px-6 sm:px-6 md:px-4 lg:px-0 pb-20 sm-pb-20 md:pb-0 lg:pb-0"
    >
      <div
        class="relative w-full max-w-screen-lg mx-auto flex justify-center items-center min-h-[calc(100vh-10rem)]"
      >
        <!-- Left Button -->
        <button
          v-if="currentPage > 1 && windowWidth >= 1024"
          @click="prevPage"
          class="hidden lg:flex items-center justify-center absolute left-0 top-1/2 z-20 -translate-y-1/2 -ml-20 p-3 border border-white/40 hover:bg-green1 duration-300 text-white rounded-full shadow-md transition bg-white/20 backdrop-blur-md group"
          :class="{ 'animate-bounce': currentPage > 1 }"
        >
          <icon name="left" />
          <span
            class="absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap text-white text-md font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            Previous
          </span>
        </button>

        <!-- Responsive Cards Grid -->
        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 justify-center items-center w-full transition-all duration-300 ease-in-out"
        >
          <div
            v-for="(system, index) in paginatedSystems"
            :key="system.id"
            class="flex flex-col items-center cursor-pointer transform transition-all duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)] bg-white/10 backdrop-blur-lg rounded-[20px] shadow-lg p-3 text-center hover:shadow-2xl hover:scale-[1.04]"
            :class="[
              {
                'opacity-0 translate-y-10': !loaded,
                'opacity-100 translate-y-0 transition-all duration-200 ease-in-out':
                  loaded,
                'pointer-events-none grayscale opacity-80':
                  disabledSystems.includes(system.id),
              },
            ]"
            :style="{ transitionDelay: `${index * 100}ms` }"
            @click="goToSystem(system)"
          >
            <img
              :src="require(`../assets/img/${system.image_filename}`)"
              :alt="system.description"
              class="rounded-[15px] w-full h-auto object-cover aspect-[4/3]"
            />
          </div>
        </div>

        <!-- Right Button -->
        <button
          v-if="currentPage < totalPages && windowWidth >= 1024"
          @click="nextPage"
          class="hidden lg:flex items-center justify-center absolute right-0 top-1/2 z-20 -translate-y-1/2 -mr-20 p-3 border border-white/40 hover:bg-green1 duration-300 text-white rounded-full shadow-md transition bg-white/20 backdrop-blur-md group"
          :class="{ 'animate-bounce': currentPage < totalPages }"
        >
          <icon name="right" />
          <span
            class="absolute -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap text-white text-md font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            Next
          </span>
        </button>
      </div>

      <!-- Modals -->
      <settingmodal v-if="isSettingModal" @close="toggleSetting" class="z-50" />
      <PortalRoles
        v-if="isSelectRoles"
        :data="selected_system"
        @close="isSelectRoles = false"
      />
      <NoAccountModal
        v-if="isNoAccount"
        :system="noAccountSystem"
        @close="toggleNoAccount"
      />

      <OngoingSystem
        v-if="isOngoingModal"
        @close="isOngoingModal = false"
        class="z-50"
      />
    </main>

    <!-- Fixed Footer -->
    <footer
      class="fixed w-full bottom-0 left-0 bg-green1/30 backdrop-blur-xl border-t border-white/20 text-white flex justify-center items-center px-6 md:px-12 py-3 z-50"
    >
      <p class="text-sm md:text-base text-center font-light">
        © 2021-{{ currentYear }} Davao del Norte State College | Systems
        Development Unit. All rights reserved.
      </p>
    </footer>
  </div>
</template>

<script>
import settingmodal from "./modals/portal-setting-modal.vue";
// import navigation from "./portal-navigation.vue";
import PortalRoles from "./modals/portal-roles.vue";
import NoAccountModal from "./alert/portal-no-account.vue";
import OngoingSystem from "./alert/portal-ongoing-system.vue";
import icon from "./icon.vue";

export default {
  name: "SystemPages",
  components: {
    // navigation,
    settingmodal,
    PortalRoles,
    NoAccountModal,
    OngoingSystem,
    icon,
  },
  data() {
    return {
      isSettingModal: false,
      systems_data: [],
      isSelectRoles: false,
      loaded: false,
      selected_system: null,
      isNoAccount: false,
      noAccountSystem: null,
      isOngoingModal: false,
      disabledSystems: [],
      visible: false,
      currentYear: new Date().getFullYear(),
      // Pagination
      currentPage: 1,
      itemsPerPage: 9,

      // Window size tracking
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.systems_data.length / this.itemsPerPage);
    },
    paginatedSystems() {
      // Show all systems for small screens, paginate for large
      if (this.windowWidth < 1024) {
        return this.systems_data;
      }
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.systems_data.slice(start, start + this.itemsPerPage);
    },
  },
  methods: {
    toggleNoAccount() {
      this.isNoAccount = !this.isNoAccount;
      this.noAccountSystem = null;
    },
    toggleSetting() {
      this.isSettingModal = !this.isSettingModal;
    },
    toggleSelectRoles() {
      this.isSelectRoles = !this.isSelectRoles;
    },
    fetchEncryptedAccounts() {
      this.encryptedUsername = sessionStorage.getItem("username") || "";
      this.encryptedPassword = sessionStorage.getItem("password") || "";
    },
    async getAllSystems() {
      try {
        this.axiosCall("/systems", "GET").then((res) => {
          this.systems_data = res.data;
        });
        console.log("Systems Data", this.systems_data);
      } catch (error) {
        console.error("Error fetching systems:", error);
      }
    },

    async goToSystem(data) {
      // 🔸 Check if system is ongoing (id 5 or 6)
      if (data.id === 5) {
        this.isOngoingModal = true;
        return;
      }

      if (data.category === 1) {
        window.open(data.url);
        return;
      }

      this.isNoAccount = false;
      this.isSelectRoles = false;

      try {
        let f_data = {
          account_id: this.$store.state.user.id,
          system_id: data.id,
        };
        this.axiosCall(
          "/credentials/getCredentialByAccount",
          "POST",
          f_data,
        ).then((res) => {
          this.credentials = res.data;

          if (this.credentials.length > 1) {
            this.isSelectRoles = true;
            this.selected_system = {
              system_data: data,
              credentials: this.credentials,
            };
          } else if (this.credentials.length === 1) {
            // window.open(
            //   ${data.url}?email=${this.credentials[0].email}&pass=${this.credentials[0].password},
            //   "_blank"
            // );
            // this.getAuthToken(
            //   data.login_api,
            //   this.credentials[0].email,
            //   this.credentials[0].password
            // );
            // let data2 = new FormData();
            // data2.append("email", this.credentials[0].email);
            // data2.append("password", this.credentials[0].password);
            let data2 = {
              email: this.credentials[0].email,
              password: this.credentials[0].password,
            };
            this.axiosCall2(data.login_api, "POST", data2).then((res) => {
              console.log(res);
              if (res.data) {
                const token = this.findPropertyValue(res.data, "token");
                if (token) {
                  window.open(data.url + "?token=" + token, "_blank");
                }
              }
            });
          } else {
            this.isNoAccount = true;
            this.noAccountSystem = data;

            if (!this.disabledSystems.includes(data.id)) {
              this.disabledSystems.push(data.id);
            }
          }
        });
      } catch (error) {
        console.error("No account found for this system:", data);
      }
    },
    getAuthToken(url, email, password) {
      let data = new FormData();
      data.append("email", email);
      data.append("password", password);
      this.axiosCall2(url, "POST", data).then((res) => {
        console.log(res.data);
        if (res.data) {
          const token = this.findPropertyValue(res.data, "token");
          if (token) {
            window.open(url + "?token=" + token, "_blank");
          }
        }
      });
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.resetLoaded();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.resetLoaded();
      }
    },
    resetLoaded() {
      this.loaded = false;
      setTimeout(() => {
        this.loaded = true;
      }, 100);
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth;
    },
  },
  created() {
    this.getAllSystems();
    this.fetchEncryptedAccounts();
  },
  mounted() {
    window.addEventListener("resize", this.updateWindowWidth);
    setTimeout(() => {
      this.loaded = true;
    }, 500);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateWindowWidth);
  },
};
</script>

<style scoped>
@media (max-width: 640px) {
  .feature-card {
    padding: 0.75rem;
  }
}

@media (min-width: 1280px) {
  .feature-card img {
    border-radius: 25px;
  }
}

/* Optional enhancements */
button {
  transition: transform 0.2s ease-in-out;
}
button:hover {
  transform: scale(1.05);
}
</style>
